/* ==========================================================================
   Gild & Ink — homepage motion layer
   Implements design_handoff_homepage §Motion.

   Everything here is additive. The page is complete, readable and correctly
   styled with this file blocked, and every effect sits behind the
   prefers-reduced-motion gate. Reveals only ever *add* a from-state, which is
   why the `gi-reveal-on` class is set from script rather than shipped in CSS.

   One passive scroll listener does all scroll work inside a single rAF.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__giHomeMotion) return;
  window.__giHomeMotion = true;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    initReveals();
    initScroll();
    initHero();
    initFoil();
  });

  /* ---- 1. scroll reveals ------------------------------------------------ */

  function initReveals() {
    var nodes = document.querySelectorAll('.gi-home [data-reveal]');
    if (!nodes.length) return;

    // Reduced motion, no observer support, or reveals turned off in the
    // theme editor: leave everything visible exactly as the CSS renders it.
    if (reduced || !('IntersectionObserver' in window)) return;
    if (!document.querySelector('.gi-home[data-reveals="true"]')) return;

    document.documentElement.classList.add('gi-reveal-on');

    var seen = new WeakSet();

    // An element's stagger delay is its index among its data-reveal siblings,
    // capped at 5 so a long grid never stalls.
    nodes.forEach(function (el) {
      var siblings = el.parentNode
        ? el.parentNode.querySelectorAll(':scope > [data-reveal]')
        : [];
      var index = Array.prototype.indexOf.call(siblings, el);
      if (index < 0) index = 0;
      el.style.animationDelay = Math.min(index, 5) * 95 + 'ms';
    });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        if (seen.has(entry.target)) return;
        seen.add(entry.target);
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.12 });

    nodes.forEach(function (el) { io.observe(el); });
  }

  /* ---- 2. progress bar + parallax, one listener, one rAF ---------------- */

  function initScroll() {
    var fill = document.querySelector('[data-gi-progress-fill]');
    var parallax = reduced
      ? []
      : Array.prototype.slice.call(document.querySelectorAll('.gi-home [data-parallax] img'));

    if (!fill && !parallax.length) return;

    // This theme scrolls an inner wrapper (.page-wrapper) rather than the
    // document, so window.scrollY never moves and a scroll listener on the
    // window never fires. Find the real scroller by walking up from the page
    // instead of assuming it is the window.
    var scroller = (function () {
      var node = document.querySelector('.gi-home');
      while (node) {
        node = node.parentElement;
        if (!node || node === document.body || node === document.documentElement) break;
        var oy = window.getComputedStyle(node).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight + 1) {
          return node;
        }
      }
      return null;
    })();

    var queued = false;

    function paint() {
      queued = false;

      if (fill) {
        var top, max;
        if (scroller) {
          top = scroller.scrollTop;
          max = scroller.scrollHeight - scroller.clientHeight;
        } else {
          top = window.scrollY;
          max = document.documentElement.scrollHeight - window.innerHeight;
        }
        var ratio = max > 0 ? top / max : 0;
        if (ratio < 0) ratio = 0;
        if (ratio > 1) ratio = 1;
        fill.style.transform = 'scaleX(' + ratio + ')';
      }

      // Rects are viewport-relative either way, so the parallax maths is the
      // same whichever element is doing the scrolling.
      for (var i = 0; i < parallax.length; i++) {
        var img = parallax[i];
        var box = img.getBoundingClientRect();
        if (box.bottom < -200 || box.top > window.innerHeight + 200) continue;
        var centred = (box.top + box.height / 2 - window.innerHeight / 2) / window.innerHeight;
        img.style.transform = 'scale(1.1) translateY(' + (-centred * 26).toFixed(2) + 'px)';
      }
    }

    function onScroll() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(paint);
    }

    // scroll does not bubble, so the listener goes on whatever is actually
    // scrolling — the inner wrapper here, the window on a conventional theme.
    (scroller || window).addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    paint();
  }

  /* ---- 3. hero gallery -------------------------------------------------- */

  function initHero() {
    var root = document.querySelector('[data-gi-hero]');
    if (!root) return;

    var slides = root.querySelectorAll('[data-gi-hero-slide]');
    var tabs = document.querySelectorAll('[data-gi-hero-tab]');
    var caption = document.querySelector('[data-gi-hero-caption]');
    if (slides.length < 2) return;

    var index = 0;
    var timer = null;

    function show(next, quiet) {
      index = (next + slides.length) % slides.length;

      for (var i = 0; i < slides.length; i++) {
        slides[i].classList.toggle('is-active', i === index);
      }
      for (var j = 0; j < tabs.length; j++) {
        tabs[j].setAttribute('aria-pressed', j === index ? 'true' : 'false');
      }
      if (caption) {
        caption.textContent = slides[index].getAttribute('data-caption') || '';
        // Restart the fade by forcing a reflow between class changes.
        caption.classList.remove('is-changing');
        void caption.offsetWidth;
        if (!reduced && !quiet) caption.classList.add('is-changing');
      }
    }

    function stop() {
      if (timer) { window.clearInterval(timer); timer = null; }
    }

    for (var k = 0; k < tabs.length; k++) {
      (function (n) {
        tabs[n].addEventListener('click', function () {
          stop();          // a tab click cancels autoplay for the session
          show(n);
        });
      })(k);
    }

    show(0, true);

    if (!reduced && root.getAttribute('data-autoplay') === 'true') {
      timer = window.setInterval(function () { show(index + 1); }, 5600);
    }
  }

  /* ---- 4. foil swatches --------------------------------------------------
     Clicking a swatch swaps the band image to that colour's own photo
     (data-image / data-image-2x, from the block's image setting) and moves
     aria-pressed. With JavaScript blocked the first swatch's image is
     already rendered server-side, so the band is never empty. */

  function initFoil() {
    var root = document.querySelector('[data-gi-foil]');
    if (!root) return;

    var swatches = root.querySelectorAll('[data-gi-foil-swatch]');
    var image = root.querySelector('[data-gi-foil-image]');
    if (!swatches.length) return;

    function select(index) {
      for (var i = 0; i < swatches.length; i++) {
        swatches[i].setAttribute('aria-pressed', i === index ? 'true' : 'false');
      }

      if (!image) return;
      var src = swatches[index].getAttribute('data-image');
      if (!src) return; // no image on this swatch — leave the band as-is

      var src2x = swatches[index].getAttribute('data-image-2x');
      var alt = swatches[index].getAttribute('data-image-alt');
      image.setAttribute('src', src);
      image.setAttribute('srcset', src + ' 700w' + (src2x ? ', ' + src2x + ' 1400w' : ''));
      if (alt) image.setAttribute('alt', alt);
    }

    for (var n = 0; n < swatches.length; n++) {
      (function (i) {
        swatches[i].addEventListener('click', function () { select(i); });
      })(n);
    }

    select(0);
  }
})();